import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import {
  PRO_TRIAL_MAX_DAYS,
  parseProTrialDays,
  proTrialDateTime,
  proTrialEndFor,
  proTrialOfferSummary,
  proTrialStatusLabel,
  type ProTrial,
  type ProTrialOffer,
} from "@/lib/viewerProTrial";

const CONTROLE =
  "rounded border border-ink-700 bg-ink-800 px-2 py-1 text-xs text-ink-200 focus:outline-none focus:ring-1 focus:ring-ink-500";

const BOTAO =
  "rounded border border-ink-700 bg-ink-800 px-3 py-1 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40";

const BOTAO_PEQUENO =
  "rounded border border-ink-700 px-2 py-0.5 text-[11px] text-ink-400 hover:border-ink-500 hover:text-ink-100 disabled:cursor-not-allowed disabled:opacity-40";

const BOTAO_ARMADO =
  "rounded border border-red-700 bg-red-950/50 px-2 py-0.5 text-[11px] text-red-300 hover:bg-red-900/50";

const COLUNAS =
  "minmax(11rem,1.5fr) minmax(11rem,1.5fr) 8.5rem 8.5rem 10.5rem minmax(9rem,1fr) 10.5rem";

function mensagemDe(erro: unknown, padrao: string): string {
  return erro instanceof Error && erro.message ? erro.message : padrao;
}

type Gesto = "encerrar" | "remover";

/**
 * Testes do Pro (out/2026): as empresas que pediram o teste grátis do Viewer
 * Pro pelo botão do link, quem pediu, quando e até quando.
 *
 * Só administrador. O prazo se mexe aqui — em dias, contados do começo —, e
 * «Encerrar» e «Remover» fazem os links da empresa voltarem ao Starter no
 * próximo pedido; remover é também o que deixa a empresa pedir de novo. A
 * oferta (ligada, empresas de teste, dias) mora nas Configurações; o cabeçalho
 * só a mostra. A régua mora em `lib/viewerProTrial.ts`.
 */
export function ProTrials() {
  const [testes, setTestes] = useState<ProTrial[]>([]);
  const [oferta, setOferta] = useState<ProTrialOffer | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [recarga, setRecarga] = useState(0);

  // O prazo digitado em cada linha, até salvar.
  const [rascunhos, setRascunhos] = useState<Record<string, string>>({});
  const [ocupada, setOcupada] = useState<string | null>(null);
  // Encerrar e remover são em dois cliques: o primeiro arma, o segundo faz.
  const [armado, setArmado] = useState<{ id: string; gesto: Gesto } | null>(null);

  useEffect(() => {
    const controle = new AbortController();
    setCarregando(true);
    setErro(null);
    api
      .listProTrials(controle.signal)
      .then((lista) => {
        setTestes(lista.items);
        setOferta(lista.offer);
        setRascunhos({});
      })
      .catch((falha) => {
        if (!controle.signal.aborted) setErro(mensagemDe(falha, "Não foi possível carregar os testes."));
      })
      .finally(() => {
        if (!controle.signal.aborted) setCarregando(false);
      });
    return () => controle.abort();
  }, [recarga]);

  const trocar = (atualizado: ProTrial) => {
    setTestes((lista) => lista.map((item) => (item.id === atualizado.id ? atualizado : item)));
    setRascunhos(({ [atualizado.id]: _descartado, ...resto }) => resto);
  };

  const salvarPrazo = async (teste: ProTrial) => {
    const dias = parseProTrialDays(rascunhos[teste.id] ?? "");
    if (dias === null || dias === teste.days) return;
    setOcupada(teste.id);
    try {
      trocar(await api.setProTrialDays(teste.id, dias));
    } catch (falha) {
      setErro(mensagemDe(falha, "Não foi possível mudar o prazo."));
    } finally {
      setOcupada(null);
    }
  };

  const executar = async (teste: ProTrial, gesto: Gesto) => {
    if (armado?.id !== teste.id || armado.gesto !== gesto) {
      setArmado({ id: teste.id, gesto });
      return;
    }
    setArmado(null);
    setOcupada(teste.id);
    try {
      if (gesto === "encerrar") {
        trocar(await api.endProTrial(teste.id));
      } else {
        await api.deleteProTrial(teste.id);
        setTestes((lista) => lista.filter((item) => item.id !== teste.id));
      }
    } catch (falha) {
      setErro(mensagemDe(falha, gesto === "encerrar" ? "Não foi possível encerrar." : "Não foi possível remover."));
    } finally {
      setOcupada(null);
    }
  };

  const emCurso = testes.filter((teste) => proTrialStatusLabel(teste) !== "Encerrado").length;

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-ink-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink-100">Testes do Pro</h1>
          <p className="mt-0.5 text-xs text-ink-400">
            Empresas testando o Viewer Pro de graça: quem pediu, quando começou e até quando vai.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRecarga((n) => n + 1)}
          disabled={carregando}
          className="rounded border border-ink-700 bg-ink-800 px-3 py-1.5 text-xs text-ink-300 hover:border-ink-500 hover:text-ink-100 disabled:opacity-50"
        >
          ↺ Atualizar
        </button>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-4">
        {oferta && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg border border-ink-700 bg-ink-850 px-3 py-2">
            <p className="text-xs text-ink-300">
              <span
                className={`mr-2 inline-block h-2 w-2 rounded-full align-middle ${
                  oferta.enabled ? "bg-emerald-400" : oferta.test_organizations.length ? "bg-amber-400" : "bg-ink-500"
                }`}
                aria-hidden="true"
              />
              {proTrialOfferSummary(oferta)}
            </p>
            <Link to="/settings" className="text-xs text-ink-400 underline-offset-2 hover:text-ink-100 hover:underline">
              Mudar em Configurações › Visualizador
            </Link>
          </div>
        )}

        {erro && (
          <p className="flex shrink-0 items-center justify-between rounded border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
            <span>{erro}</span>
            <button type="button" onClick={() => setRecarga((n) => n + 1)} className={BOTAO}>
              Tentar de novo
            </button>
          </p>
        )}

        <div className="flex min-h-[12rem] flex-1 flex-col overflow-hidden rounded-lg border border-ink-700">
          <div className="flex-1 overflow-auto">
            <div className="min-w-[66rem]">
              <div
                className="sticky top-0 grid select-none gap-x-3 border-b border-ink-700 bg-ink-850 px-3 py-2 text-xs font-medium uppercase tracking-wide text-ink-400"
                style={{ gridTemplateColumns: COLUNAS }}
              >
                <span>Empresa</span>
                <span>Pedido por</span>
                <span>Começou</span>
                <span>Termina</span>
                <span>Prazo</span>
                <span>Situação</span>
                <span />
              </div>

              {carregando && testes.length === 0 && (
                <div className="flex items-center justify-center py-12 text-sm text-ink-500">Carregando…</div>
              )}
              {!carregando && !erro && testes.length === 0 && (
                <div className="flex items-center justify-center px-6 py-12 text-center text-sm text-ink-500">
                  Nenhuma empresa pediu o teste ainda.
                </div>
              )}

              {testes.map((teste) => {
                const situacao = proTrialStatusLabel(teste);
                const ativo = situacao !== "Encerrado";
                const rascunho = rascunhos[teste.id];
                const diasDigitados = rascunho === undefined ? teste.days : parseProTrialDays(rascunho);
                const mudou = rascunho !== undefined && diasDigitados !== null && diasDigitados !== teste.days;
                const fimPrevisto = mudou && diasDigitados !== null ? proTrialEndFor(teste.started_at, diasDigitados) : null;
                const ocupadaAqui = ocupada === teste.id;
                return (
                  <div
                    key={teste.id}
                    className="grid items-center gap-x-3 border-b border-ink-800 px-3 py-2 text-xs last:border-b-0"
                    style={{ gridTemplateColumns: COLUNAS }}
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink-100" title={teste.organization_name ?? undefined}>
                        {teste.organization_name ?? "Sem nome"}
                      </span>
                      <span className="block truncate font-mono text-[11px] text-ink-500" title={teste.organization_id}>
                        {teste.organization_id}
                      </span>
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-ink-200" title={teste.requested_by_name}>
                        {teste.requested_by_name}
                      </span>
                      <span className="block truncate text-[11px] text-ink-500" title={teste.requested_by_email ?? undefined}>
                        {teste.requested_by_email ?? "—"}
                      </span>
                    </span>
                    <span className="tabular-nums text-ink-400">{proTrialDateTime(teste.started_at)}</span>
                    <span className="tabular-nums text-ink-400" title={teste.updated_by ? `Prazo mudado por ${teste.updated_by}` : undefined}>
                      {proTrialDateTime(fimPrevisto ?? teste.ends_at)}
                      {fimPrevisto ? <span className="block text-[10px] text-amber-300">ao salvar</span> : null}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <input
                        type="number"
                        min={1}
                        max={PRO_TRIAL_MAX_DAYS}
                        step={1}
                        value={rascunho ?? String(teste.days)}
                        onChange={(event) =>
                          setRascunhos((atuais) => ({ ...atuais, [teste.id]: event.target.value }))
                        }
                        onKeyDown={(event) => {
                          if (event.key === "Enter") void salvarPrazo(teste);
                        }}
                        disabled={ocupadaAqui}
                        aria-label={`Prazo do teste de ${teste.organization_name ?? teste.organization_id}, em dias`}
                        className={`w-16 tabular-nums ${CONTROLE} ${
                          rascunho !== undefined && diasDigitados === null ? "border-red-700" : ""
                        }`}
                      />
                      <span className="text-ink-500">dias</span>
                      {mudou && (
                        <button
                          type="button"
                          onClick={() => void salvarPrazo(teste)}
                          disabled={ocupadaAqui}
                          className="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-0.5 text-[11px] text-emerald-200 hover:bg-emerald-800/50"
                        >
                          Salvar
                        </button>
                      )}
                    </span>
                    <span className={ativo ? "font-medium text-emerald-300" : "text-ink-500"}>{situacao}</span>
                    <span className="flex justify-end gap-1.5">
                      {ativo && (
                        <button
                          type="button"
                          onClick={() => void executar(teste, "encerrar")}
                          onBlur={() => setArmado((atual) => (atual?.id === teste.id && atual.gesto === "encerrar" ? null : atual))}
                          disabled={ocupadaAqui}
                          className={armado?.id === teste.id && armado.gesto === "encerrar" ? BOTAO_ARMADO : BOTAO_PEQUENO}
                          title="Os links da empresa voltam ao Starter no próximo pedido"
                        >
                          {armado?.id === teste.id && armado.gesto === "encerrar" ? "Confirmar" : "Encerrar"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void executar(teste, "remover")}
                        onBlur={() => setArmado((atual) => (atual?.id === teste.id && atual.gesto === "remover" ? null : atual))}
                        disabled={ocupadaAqui}
                        className={armado?.id === teste.id && armado.gesto === "remover" ? BOTAO_ARMADO : BOTAO_PEQUENO}
                        title="Apaga o registro: a empresa volta ao Starter e pode pedir o teste de novo"
                      >
                        {armado?.id === teste.id && armado.gesto === "remover" ? "Confirmar" : "Remover"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-ink-700 bg-ink-850 px-3 py-2 text-xs text-ink-400">
            {testes.length === 0
              ? "Nenhum teste"
              : `${testes.length} ${testes.length === 1 ? "empresa" : "empresas"} · ${emCurso} em teste agora`}
          </div>
        </div>
      </div>
    </section>
  );
}
